/**
 * Create and initialize Tako accordion
 */

/* -----------------------------------------------------------------------------
 * Tako Accordion
 * -------------------------------------------------------------------------- */
import { InstanceLoggerArgs } from '../types/logger';
import { showElement } from './utils/showElement';
import { hideElement } from './utils/hideElement';
import { takoUUID } from './utils/takoUUID';
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Accordion';

const accordionInstances = new Map<
    HTMLElement,
    { open: () => void; close: () => void }
>();

export const init = (unresolvedScope: string | Element | unknown) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const openIcon = '<i class="far fa-angle-down" aria-hidden="true"></i>';
    const closeIcon = '<i class="far fa-angle-up" aria-hidden="true"></i>';
    const DATA_OPEN = 'data-open';

    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    const allTakoAccordions =
        scope.querySelectorAll<HTMLElement>('.tako-accordion');

    allTakoAccordions.forEach((accordion) => {
        try {
            const instanceLoggerArgs: InstanceLoggerArgs = {
                ...defaultLoggerArgs,
                element: accordion,
            };
            const accordionHeader = accordion.querySelector<HTMLElement>(
                '.tako-accordion__header',
            );
            const accordionBody = accordion.querySelector<HTMLElement>(
                '.tako-accordion__body',
            );

            // if no body or header has been provided then accordion will not work
            if (!accordionBody || !accordionHeader) {
                logger.warn(
                    'A Tako Accordion must have a header with the class of `tako-accordion__header` and a body with the class of `tako-accordion__body`. Omitting component...',
                    instanceLoggerArgs,
                );

                return;
            }

            if (!accordionBody.id) {
                accordionBody.setAttribute('id', takoUUID('accordion-body'));
            }

            /**
             * data-expanded-header attribute is @deprecated
             */
            const expandedHeaderText = accordionHeader.dataset.expandedHeader;
            if (expandedHeaderText) {
                logger.warn(
                    'The `data-expanded-header` attribute is deprecated. Please use the `data-open-accordion-title` attribute instead.',
                    instanceLoggerArgs,
                );
            }
            const openHeaderText =
                expandedHeaderText ||
                accordionHeader.dataset.openAccordionTitle;
            const closedHeaderText = accordionHeader.innerHTML;

            /**
             * tako-accordion--expanded class is @deprecated
             */
            const accordionWithLegacyClass =
                scope.querySelectorAll<HTMLElement>(
                    '.tako-accordion--expanded',
                );

            // Replace deprecated `tako-accordion--expanded` class with `data-open` attribute
            if (accordionWithLegacyClass.length) {
                logger.warn(
                    'The `tako-accordion--expanded` class is deprecated. Please use the `data-open` attribute instead.',
                    instanceLoggerArgs,
                );
                accordion.setAttribute(DATA_OPEN, 'true');
            }

            const accordionIsOpen =
                accordion.hasAttribute(DATA_OPEN) &&
                accordion.getAttribute(DATA_OPEN) !== 'false';

            accordionHeader.setAttribute('tabindex', '0');
            accordionHeader.setAttribute('role', 'button');
            accordionHeader.setAttribute('aria-controls', accordionBody.id);
            accordionHeader.setAttribute(
                'aria-expanded',
                accordionIsOpen.toString(),
            );
            accordion.setAttribute(DATA_OPEN, accordionIsOpen.toString());

            //set icon based on initial open state
            const icon = document.createElement('span');
            icon.classList.add('tako-icon', 'tako-accordion__toggle-icon');
            icon.innerHTML = accordionIsOpen ? closeIcon : openIcon;
            accordionHeader.appendChild(icon);

            //set display of body based on initial expanded state
            if (accordionIsOpen) {
                showElement(accordionBody);
            } else {
                hideElement(accordionBody);
            }
            accordionBody.setAttribute('tabindex', '0');
            accordionBody.setAttribute('id', accordionBody.id);

            const open = () => {
                showElement(accordionBody);
                accordion.setAttribute(DATA_OPEN, 'true');
                const toggleIcon = accordionHeader.querySelector<HTMLElement>(
                    '.tako-accordion__toggle-icon',
                );

                //if open and closed headers differ then set styles of each accordingly
                if (toggleIcon) {
                    toggleIcon.innerHTML = closeIcon;
                }
                if (openHeaderText) {
                    // if header button contains an element that is not the accordion icon then place expanded text inside tags
                    if (
                        accordionHeader.firstElementChild &&
                        !accordionHeader.firstElementChild.classList.contains(
                            'tako-accordion__toggle-icon',
                        )
                    ) {
                        accordionHeader.firstElementChild.innerHTML =
                            openHeaderText;
                    } else {
                        accordionHeader.innerHTML = openHeaderText;
                    }
                    if (toggleIcon) {
                        accordionHeader.appendChild(toggleIcon);
                    }
                }
                accordionHeader.setAttribute('aria-expanded', 'true');

                // Emit a custom event when the accordion is opened
                const openEvent = new CustomEvent('takoAccordionOpen', {
                    detail: {
                        accordion,
                        accordionHeader,
                        id: accordion.id,
                    },
                });
                accordion.dispatchEvent(openEvent);
            };

            const close = () => {
                hideElement(accordionBody);
                accordion.setAttribute(DATA_OPEN, 'false');
                const toggleIcon = accordionHeader.querySelector<HTMLElement>(
                    '.tako-accordion__toggle-icon',
                );

                if (openHeaderText) {
                    accordionHeader.innerHTML = closedHeaderText;
                    if (toggleIcon) {
                        accordionHeader.appendChild(toggleIcon);
                    }
                }
                if (toggleIcon) {
                    toggleIcon.innerHTML = openIcon;
                }
                accordionHeader.setAttribute('aria-expanded', 'false');

                // Emit a custom event when the accordion is closed
                const closeEvent = new CustomEvent('takoAccordionClose', {
                    detail: {
                        accordion,
                        accordionHeader,
                        id: accordion.id,
                    },
                });
                accordion.dispatchEvent(closeEvent);
            };

            const toggleAccordion = () => {
                if (accordionBody.style.display === 'none') {
                    open();
                } else {
                    close();
                }
            };

            accordionHeader.addEventListener('click', toggleAccordion);

            // Store the instance for external control via `toggle`
            accordionInstances.set(accordion, { open, close });
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: accordion,
            });
        }
    });
};

/**
 * Tako Accordion Functions
 */

const debounceDelay = 100;
const debounceTimeouts = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Toggles the visibility of one or more Tako Accordion components.
 *
 * @param {string} selector - Document query selector (id or class) to target the accordion(s).
 *                            Accepts an id (e.g., '#my-accordion') or a class (e.g., '.my-accordion-class').
 * @param {boolean|null} [open=null] - Optional. If `true`, explicitly open the accordion(s).
 *                                     If `false`, explicitly close the accordion(s).
 *                                     If `null` (default), the accordion's current state will be toggled.
 *
 * @example
 * // Toggle based on the current state (open if closed, close if open):
 * toggleAccordion('.my-accordion-class');
 *
 * @example
 * // Explicitly open the accordion(s):
 * toggleAccordion('#my-accordion-id', true);
 *
 * @example
 * // Explicitly close the accordion(s):
 * toggleAccordion('.my-accordion-class', false);
 */
export const toggle = (selector: string, open: boolean | null = null) => {
    const logger = getLogger();

    // Support id's without the # prefix
    if (selector[0] !== '#' && selector[0] !== '.') {
        selector = `#${selector}`;
    }

    // Get all elements that match the selector (could be multiple if class selector is used)
    const takoAccordionElements =
        document.querySelectorAll<HTMLElement>(selector);

    if (!takoAccordionElements.length) {
        logger.warn(
            `No Tako Accordion components found with the selector "${selector}".`,
        );
        return;
    }

    takoAccordionElements.forEach((takoAccordionElement) => {
        const instance = accordionInstances.get(takoAccordionElement);

        if (!instance) {
            logger.warn(
                `No instance found for the element with the selector "${selector}". Make sure the Tako Accordion has been initialized.`,
            );
            return;
        }

        // Clear any existing timeout for this accordion
        if (debounceTimeouts.has(takoAccordionElement)) {
            clearTimeout(debounceTimeouts.get(takoAccordionElement));
        }

        const shouldOpen =
            open !== null
                ? open
                : takoAccordionElement.getAttribute('data-open') === 'false'; // If `open` is null, toggle based on current state

        // Handle debounce delay to ensure proper timing
        debounceTimeouts.set(
            takoAccordionElement,
            setTimeout(() => {
                if (shouldOpen) {
                    instance.open();
                } else {
                    instance.close();
                }

                // Remove the timeout once it's executed
                debounceTimeouts.delete(takoAccordionElement);
            }, debounceDelay),
        );
    });
};
